/**
 * Async iterator result shape returned by the reader-based fallback below —
 * kept local since `AsyncIterator<T>`'s own `next()`/`return()` signatures
 * are already exactly this, but re-declaring keeps the file dependency-free.
 */
type ReadableStreamLike<T> = { getReader(): ReadableStreamDefaultReaderLike<T> };
type ReadableStreamDefaultReaderLike<T> = {
  read(): Promise<{ done: boolean; value?: T }>;
  releaseLock(): void;
};

/**
 * Builds a spec-shaped `AsyncIterator` over a `ReadableStream` via its
 * `getReader()`/`read()`/`releaseLock()` primitives — the same steps the
 * WHATWG Streams spec's own `ReadableStream.prototype[Symbol.asyncIterator]`
 * performs internally. Used only as the polyfill body in
 * {@link installReadableStreamAsyncIteratorPolyfill}; not exported.
 *
 * @param this - The `ReadableStream` instance being iterated (bound via
 *   `stream[Symbol.asyncIterator]()`, exactly like a real method call).
 */
function readableStreamAsyncIteratorPolyfill<T>(this: ReadableStreamLike<T>): AsyncIterator<T> {
  const reader = this.getReader();
  return {
    async next(): Promise<IteratorResult<T>> {
      const { done, value } = await reader.read();
      if (done) {
        reader.releaseLock();
        return { done: true, value: undefined };
      }
      return { done: false, value: value as T };
    },
    async return(value?: T): Promise<IteratorResult<T>> {
      reader.releaseLock();
      return { done: true, value: value as T };
    },
  };
}

/**
 * Installs `ReadableStream.prototype[Symbol.asyncIterator]` when the current
 * JS engine doesn't already provide one — a no-op everywhere it does.
 *
 * @remarks
 * **Why this exists (2026-08-07):** a live Sentry event from a native iOS
 * build showed `PdfFillService.findItemMatches()` throwing inside
 * `pdfjs-dist`'s own `getTextContent()` — `TypeError: undefined is not a
 * function (near '...n of t...')`. `getTextContent()` (legacy build,
 * `pdf.mjs`) is implemented as `for await (const value of readableStream)
 * { ... }` — minified to `for await(const n of t)`, matching the error's
 * source snippet exactly. `for await...of` calls
 * `readableStream[Symbol.asyncIterator]()`; on a `ReadableStream` with no
 * such method the call site itself is `undefined`, producing precisely this
 * "undefined is not a function" error. WebKit (Safari/WKWebView — the engine
 * every Capacitor iOS build uses, confirmed by the `capacitor://localhost`
 * origin in the stack trace) only added `ReadableStream` async-iteration
 * support in a comparatively recent release; a device on an older
 * iOS/WKWebView build has a fully-functional `ReadableStream` that simply
 * lacks this one method — every other PDF operation (loading, rendering via
 * `PdfViewerComponent`) is unaffected since none of them iterate a stream
 * this way, which is why only signature-fill on some devices was reported
 * broken rather than PDF viewing generally.
 *
 * This cannot be fixed inside `pdfjs-dist` itself (a `node_modules`
 * dependency, overwritten on every `npm install`) — instead this polyfill is
 * installed once, at app bootstrap (`main.ts`), long before any lazy-loaded
 * feature (the affidavit screen) ever imports `pdfjs-dist`. The fallback
 * mirrors the WHATWG Streams spec's own reference algorithm for this method:
 * acquire a reader, `read()` in a loop until `done`, release the lock on
 * exhaustion or early exit (a `break`/`return` inside the consuming
 * `for await` calls the iterator's own `return()`).
 *
 * @param target - Defaults to `globalThis`. Overridable so tests can inject
 *   a fake `ReadableStream` constructor instead of mutating the real global.
 */
export function installReadableStreamAsyncIteratorPolyfill(target: typeof globalThis = globalThis): void {
  const ReadableStreamCtor = (target as { ReadableStream?: { prototype: object } }).ReadableStream;
  if (!ReadableStreamCtor) return;

  const proto = ReadableStreamCtor.prototype as { [Symbol.asyncIterator]?: unknown };
  if (typeof proto[Symbol.asyncIterator] === 'function') return;

  Object.defineProperty(proto, Symbol.asyncIterator, {
    value: readableStreamAsyncIteratorPolyfill,
    writable: true,
    configurable: true,
  });
}
